/**
 * URL-fragment sharing: encode a trajectory into the URL hash, read it back.
 *
 * Everything the app needs travels in the fragment, which never reaches the
 * server: GitHub Pages only ever serves `index.html`, so a shared link works
 * there with no routing, no rewrite rules and no 404 shim. The hash is used
 * rather than a query string so the payload (the user's data) is never sent to
 * GitHub.
 *
 * Payload format (pinned so it can be tested and evolved):
 *
 *   1. `entries.map((e) => [Math.round(e.x * 100), Math.round(e.y * 100), e.year, e.description ?? ''])`
 *   2. `JSON.stringify` of that array
 *   3. lz-string `compressToEncodedURIComponent`
 *   4. prefixed with `SHARE_VERSION + '.'`
 *
 * JSON rather than a delimiter-joined string because a description is free
 * text: it may contain `,`, `"`, `\`, `=`, `#`, `%` or `&`. JSON's own quoting
 * and escaping removes that whole class of delimiter bugs, and the punctuation
 * it costs is erased by the compression that runs next. Coordinates travel as
 * integer hundredths — the same two decimals the inputs normalise to and the
 * chart displays — so a shared link round-trips exactly.
 */

import LZString from 'lz-string'
import { isValidCoord, isValidYear } from './compass'
import { createEntryId } from './storage'
import { MAX_DESCRIPTION_LENGTH } from './types'
import type { Entry } from './types'

/** Fragment key holding the payload: `#d=<payload>`. */
export const SHARE_PARAM = 'd'

/** Payload prefix, so a future format can be detected (and refused) by name. */
export const SHARE_VERSION = 'v1'

/** Above this many characters, some apps truncate links; warn the user. */
export const SHARE_LENGTH_WARNING = 2000

/** Coordinates are carried as integer hundredths of a compass unit. */
const COORD_SCALE = 100

/**
 * `+` is replaced by `.` in the payload, and back again when decoding.
 *
 * `+` is in lz-string's URI-safe alphabet but it is also the one character that
 * link handlers and chat clients sometimes normalise to a space (that is
 * form-encoding semantics, not URL semantics), which would silently corrupt the
 * payload. `.` cannot collide: lz-string's alphabet has no `.`, and the version
 * prefix is stripped before the replacement runs.
 */
const LZ_PLUS = '+'
const PLUS_SUBSTITUTE = '.'

/** The version prefix as it appears at the start of a payload. */
const PREFIX = `${SHARE_VERSION}.`

/** One payload tuple: `[x * 100, y * 100, year, description]`. */
type ShareTuple = [number, number, number, string]

/** Encode entries as a `v1.<payload>` string that is safe inside a URL. */
export function encodeShare(entries: Entry[]): string {
  const tuples: ShareTuple[] = entries.map((entry) => [
    // `Math.round`, not truncation: 4.256 has to come back as 4.26 to match the
    // rounding the input fields apply when they commit a value.
    Math.round(entry.x * COORD_SCALE),
    Math.round(entry.y * COORD_SCALE),
    entry.year,
    entry.description ?? '',
  ])
  return PREFIX + LZString.compressToEncodedURIComponent(JSON.stringify(tuples)).replaceAll(LZ_PLUS, PLUS_SUBSTITUTE)
}

/**
 * Decode a payload back into entries.
 *
 * Total by design — the payload comes from a URL anyone can edit, so a bad one
 * returns `null` instead of throwing: no version prefix, unreadable
 * compression, invalid JSON or a non-array all mean "not a share link".
 *
 * A readable payload yields entries with fresh ids (the tuples carry none),
 * invalid tuples dropped, and descriptions truncated to
 * `MAX_DESCRIPTION_LENGTH`. A valid but empty payload yields `[]`.
 */
export function decodeShare(payload: string): Entry[] | null {
  if (typeof payload !== 'string' || !payload.startsWith(PREFIX)) return null

  // lz-string returns null for input it cannot read and can throw on some
  // malformed input; both have to come back as `null`, not a crash.
  let decompressed: unknown
  try {
    decompressed = LZString.decompressFromEncodedURIComponent(
      // Tolerate both spellings, so a link mangled by a client that rewrites
      // `+` still opens (and so do payloads written before this rule existed).
      payload.slice(PREFIX.length).replaceAll(PLUS_SUBSTITUTE, LZ_PLUS),
    )
  } catch {
    return null
  }
  if (typeof decompressed !== 'string') return null

  let parsed: unknown
  try {
    parsed = JSON.parse(decompressed)
  } catch {
    return null
  }
  if (!Array.isArray(parsed)) return null

  const entries: Entry[] = []
  const seenIds = new Set<string>()
  for (const tuple of parsed) {
    const entry = toEntry(tuple, seenIds)
    if (entry !== null) entries.push(entry)
  }
  return entries
}

/** `base` (default `location.href`) with the fragment set to the payload. */
export function buildShareUrl(entries: Entry[], base: string = location.href): string {
  const url = new URL(base)
  // The whole fragment belongs to the share payload: assigning it replaces a
  // stale `d` (or any other fragment) rather than appending a second one, and
  // an empty list clears it so an empty app has a clean URL. The payload is
  // already URI-safe (lz-string's encoded alphabet is alphanumerics, `+`, `-`
  // and `$`), so it needs no further escaping on the way in.
  url.hash = entries.length === 0 ? '' : `${SHARE_PARAM}=${encodeShare(entries)}`
  return url.toString()
}

/**
 * Read the entries out of a `location.hash`-style string, with or without the
 * leading `#`.
 *
 * Returns `null` when there is no readable `d` parameter; extra parameters are
 * tolerated, and a `%`-escaped value (as a `URLSearchParams`-built link would
 * produce) is decoded before parsing.
 */
export function readShareFromHash(hash: string): Entry[] | null {
  if (typeof hash !== 'string') return null
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash

  for (const part of fragment.split('&')) {
    const separator = part.indexOf('=')
    if (separator === -1 || part.slice(0, separator) !== SHARE_PARAM) continue

    let value = part.slice(separator + 1)
    try {
      value = decodeURIComponent(value)
    } catch {
      return null
    }
    return decodeShare(value)
  }
  return null
}

/**
 * Validate and normalise one `[x100, y100, year, description]` tuple.
 *
 * Returns `null` for anything that is not at least an `[x, y, year]` triple of
 * usable values, so a single bad tuple cannot poison the rest of the link.
 */
function toEntry(tuple: unknown, seenIds: Set<string>): Entry | null {
  if (!Array.isArray(tuple) || tuple.length < 3) return null

  // `Array.isArray` narrows to `any[]`; re-state the element type so no `any`
  // leaks out of the validation below.
  const items = tuple as unknown[]
  const [rawX, rawY, rawYear] = items
  if (typeof rawX !== 'number' || typeof rawY !== 'number') return null

  const x = rawX / COORD_SCALE
  const y = rawY / COORD_SCALE
  if (!isValidCoord(x) || !isValidCoord(y) || !isValidYear(rawYear)) return null

  const rawDescription: unknown = items[3]
  return {
    id: mintId(seenIds),
    x,
    y,
    year: rawYear,
    description: typeof rawDescription === 'string' ? truncateDescription(rawDescription) : '',
  }
}

/**
 * Mint an id for a decoded entry, unique within the payload.
 *
 * The tuples carry no ids at all, so every entry from a link gets a fresh one —
 * which is what makes the same link safe to open twice without the two lists
 * sharing ids. The id factory is the storage module's, so a shared link and
 * localStorage mint ids the same way without building a throwaway `Entry` (and
 * a `Date`) per decoded entry; the loop guards uniqueness even if the
 * platform's UUID source ever repeated itself.
 */
function mintId(seenIds: Set<string>): string {
  let id = createEntryId()
  while (seenIds.has(id)) id = createEntryId()
  seenIds.add(id)
  return id
}

/**
 * Cap a description at `MAX_DESCRIPTION_LENGTH` code points.
 *
 * By code point, not UTF-16 unit: `slice` would cut an emoji at the boundary
 * into a lone surrogate, which renders as a replacement character.
 */
function truncateDescription(text: string): string {
  const codePoints = Array.from(text)
  return codePoints.length > MAX_DESCRIPTION_LENGTH
    ? codePoints.slice(0, MAX_DESCRIPTION_LENGTH).join('')
    : text
}
